/**
 * Shared ContactsTab - reusable full-CRUD contacts panel.
 * Used by CustomerDetailPage (Partners) and TenantDetailPage (Tenants).
 *
 * Props:
 *   contacts   - initial array of contact objects
 *   onAdd      - async (data) => saved contact
 *   onUpdate   - async (contactId, data) => updated contact
 *   onDelete   - async (contactId) => void
 *   noun       - "partner" | "tenant" (used in UI copy)
 */
import React, { useState, useEffect } from 'react';
import {
  Plus, Trash2, Star, Loader2, Phone, Mail, XCircle, AlertCircle, CheckCircle2, Pencil, ArrowLeft
} from 'lucide-react';
import { Button }   from './ui/button';
import { Input }    from './ui/input';
import { Label }    from './ui/label';
import { Switch }   from './ui/switch';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge }    from './ui/badge';
import { toast }    from 'sonner';

const NAVY   = 'hsl(var(--c4p-navy-900))';
const ORANGE = 'hsl(var(--c4p-orange-600))';
const blank  = { name: '', title: '', email: '', phone: '', is_primary: false, contact_type: 'general', is_active: true, notes: '' };

export function ContactsTab({ contacts: initialContacts = [], onAdd, onUpdate, onDelete, noun = 'partner' }) {
  const [contacts,     setContacts]     = useState(initialContacts);
  const [showForm,     setShowForm]     = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [form,         setForm]         = useState(blank);
  const [saving,       setSaving]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  useEffect(() => { setContacts(initialContacts); }, [initialContacts]);

  const openAdd  = () => { setForm(blank);     setEditTarget(null); setShowForm(true); };
  const openEdit = (c) => { setForm({ ...c }); setEditTarget(c);   setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditTarget(null); setForm(blank); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editTarget) {
        const updated = await onUpdate(editTarget.id, form);
        setContacts(cs => cs.map(c => c.id === editTarget.id ? { ...c, ...updated } : c));
        toast.success('Contact updated');
      } else {
        const created = await onAdd(form);
        setContacts(cs => [...cs, created]);
        toast.success('Contact added');
      }
      closeForm();
    } catch { toast.error(editTarget ? 'Failed to update' : 'Failed to add'); }
    finally   { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try {
      const updated = await onUpdate(c.id, { is_active: !c.is_active });
      setContacts(cs => cs.map(x => x.id === c.id ? { ...x, ...updated } : x));
    } catch { toast.error('Failed to update contact'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await onDelete(deleteTarget.id);
      setContacts(cs => cs.filter(c => c.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success('Contact removed');
    } catch { toast.error('Failed to remove contact'); }
    finally   { setDeleting(false); }
  };

  const active   = contacts.filter(c => c.is_active !== false);
  const inactive = contacts.filter(c => c.is_active === false);

  const Row = ({ c }) => (
    <TableRow
      onClick={() => openEdit(c)}
      className="cursor-pointer transition-colors hover:bg-slate-50"
      style={{ opacity: c.is_active === false ? 0.55 : 1 }}
      data-testid={`contact-row-${c.id}`}
    >
      <TableCell className="font-medium text-sm">
        <div className="flex items-center gap-1.5">
          {c.name}
          {c.is_primary && <Star size={11} style={{ color: ORANGE }} fill="currentColor" />}
          {c.is_active === false && (
            <span className="text-xs px-1 rounded"
              style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-fg))' }}>Inactive</span>
          )}
        </div>
      </TableCell>
      <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{c.title || '-'}</TableCell>
      <TableCell>
        {c.email
          ? <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1 text-xs" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>
              <Mail size={11} />{c.email}
            </a>
          : <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>-</span>}
      </TableCell>
      <TableCell>
        {c.phone
          ? <span className="flex items-center gap-1 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
              <Phone size={11} />{c.phone}
            </span>
          : <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>-</span>}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1 flex-wrap">
          {c.is_primary && <Badge variant="outline" className="text-xs" style={{ background: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }}>Primary</Badge>}
          {c.contact_type === 'billing' && <Badge variant="outline" className="text-xs" style={{ background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' }}>Billing</Badge>}
        </div>
      </TableCell>
      <TableCell className="text-xs max-w-[100px] truncate" style={{ color: 'hsl(var(--muted-fg))' }}>
        {c.notes || '-'}
      </TableCell>
      <TableCell onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="sm" onClick={() => openEdit(c)}
            title="Edit contact" data-testid={`edit-contact-${c.id}`}>
            <Pencil size={12} style={{ color: 'hsl(var(--muted-fg))' }} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => toggleActive(c)}
            title={c.is_active === false ? 'Activate' : 'Deactivate'}
            data-testid={`toggle-contact-${c.id}`}>
            {c.is_active === false
              ? <CheckCircle2 size={12} style={{ color: '#16a34a' }} />
              : <XCircle     size={12} style={{ color: '#d97706' }} />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget({ id: c.id, name: c.name })}
            data-testid={`remove-contact-${c.id}`}>
            <Trash2 size={12} style={{ color: 'hsl(var(--danger))' }} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  // ── Full-screen edit / add form (replaces list when active) ─────────────────
  if (showForm) {
    return (
      <div className="space-y-6" data-testid="contact-edit-form">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={closeForm}
            className="flex items-center gap-1.5 text-sm font-medium hover:opacity-70 transition-opacity"
            style={{ color: 'hsl(var(--muted-fg))' }} data-testid="contact-form-back">
            <ArrowLeft size={14} /> Back to contacts
          </button>
          <h3 className="text-lg font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
            {editTarget ? `Edit — ${editTarget.name}` : 'Add New Contact'}
          </h3>
          <div /> {/* spacer */}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="rounded-xl border bg-white overflow-hidden"
            style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>

            {/* Name + Title */}
            <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4"
              style={{ borderBottom: '1px solid #f1f5f9' }}>
              <div className="space-y-1.5">
                <Label>Full Name *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Jane Smith" required data-testid="contact-name" className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>Title / Role</Label>
                <Input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Finance Director" data-testid="contact-title" className="h-10" />
              </div>
            </div>

            {/* Email + Phone */}
            <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4"
              style={{ borderBottom: '1px solid #f1f5f9' }}>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email || ''}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="jane@example.com" data-testid="contact-email" className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+44 7700 900000" data-testid="contact-phone" className="h-10" />
              </div>
            </div>

            {/* Contact Type */}
            <div className="px-6 py-5" style={{ borderBottom: '1px solid #f1f5f9' }}>
              <Label className="text-sm font-semibold block mb-3">Contact Type</Label>
              <div className="flex gap-3">
                {[['general', 'General', 'Standard contact'], ['billing', 'Billing', 'Appears on quotes & PDFs']].map(([val, lbl, desc]) => (
                  <button key={val} type="button"
                    onClick={() => setForm(f => ({ ...f, contact_type: val }))}
                    className="flex-1 px-4 py-3 rounded-xl border-2 text-left transition-all"
                    style={form.contact_type === val
                      ? { borderColor: val === 'billing' ? '#1d4ed8' : NAVY, background: val === 'billing' ? '#eff6ff' : 'hsl(var(--c4p-navy-900) / 0.06)' }
                      : { borderColor: '#e2e8f0', background: 'white' }}
                    data-testid={`contact-type-${val}`}>
                    <p className="text-sm font-semibold" style={{ color: form.contact_type === val ? (val === 'billing' ? '#1d4ed8' : NAVY) : '#475569' }}>{lbl}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>{desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Flags */}
            <div className="px-6 py-5 flex items-center gap-6" style={{ borderBottom: '1px solid #f1f5f9' }}>
              <div className="flex items-center gap-2.5">
                <Switch checked={!!form.is_primary}
                  onCheckedChange={v => setForm(f => ({ ...f, is_primary: v }))} id="c-primary" />
                <Label htmlFor="c-primary" className="text-sm cursor-pointer">Primary Contact</Label>
              </div>
              {editTarget && (
                <div className="flex items-center gap-2.5">
                  <Switch checked={form.is_active !== false}
                    onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} id="c-active" />
                  <Label htmlFor="c-active" className="text-sm cursor-pointer">Active</Label>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="px-6 py-5">
              <Label className="text-sm font-semibold block mb-2">Notes</Label>
              <Textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any additional notes about this contact…" className="h-20 resize-none" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-4">
            <Button type="button" variant="outline" onClick={closeForm}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.name.trim()}
              style={{ background: NAVY, color: 'white', minWidth: '140px' }}
              data-testid="contact-submit">
              {saving
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : editTarget ? 'Save Changes' : 'Add Contact'}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // ── Contacts list ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
          {active.length} active · {inactive.length} inactive
        </p>
        <Button size="sm" onClick={openAdd}
          style={{ background: ORANGE, color: 'white' }} className="gap-1"
          data-testid="add-contact-button">
          <Plus size={13} /> Add Contact
        </Button>
      </div>

      {contacts.length === 0 ? (
        <div className="rounded-xl border bg-white p-10 text-center">
          <Mail size={28} className="mx-auto mb-3" style={{ color: 'hsl(var(--border))' }} />
          <p className="text-sm font-medium" style={{ color: NAVY }}>No contacts yet</p>
          <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>Add a primary contact to get started.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"
          style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <Table>
            <TableHeader>
              <TableRow style={{ background: '#f8fafc' }}>
                <TableHead>Name</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {active.map(c =>   <Row key={c.id} c={c} />)}
              {inactive.map(c => <Row key={c.id} c={c} />)}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle size={18} style={{ color: 'hsl(var(--danger))' }} /> Remove Contact
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Remove <strong>{deleteTarget?.name}</strong> from this {noun}?
          </p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting}
              style={{ background: 'hsl(var(--danger))', color: 'white' }}
              data-testid="confirm-remove-contact">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
